import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function HiddenVancouverNeighborhoods() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "hidden-vancouver-neighborhoods";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "strathcona": "Strathcona",
    "chinatown": "Chinatown",
    "punjabi-market": "Punjabi Market",
    "commercial-drive": "Commercial Drive",
    "mount-pleasant": "Mount Pleasant",
    "southlands": "Southlands",
    "hastings-sunrise": "Hastings-Sunrise",
    "queen-elizabeth-park": "Queen Elizabeth Park",
    "pacific-spirit-park": "Pacific Spirit Park & Wreck Beach",
    "kerrisdale": "Kerrisdale",
    faq: "FAQ",
    "local-regulations": "Local Regulations",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://vancouver.ca/news-calendar/neighbourhoods.aspx",
      label: "City of Vancouver – Neighbourhoods"
    },
    {
      url: "https://www.destinationvancouver.com/",
      label: "Destination Vancouver – Official Tourism Board"
    },
    {
      url: "https://www.metrovancouver.org/services/parks/parks-greenways-reserves/pacific-spirit-regional-park",
      label: "Metro Vancouver – Pacific Spirit Park"
    }
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  const locations = [
    // Strathcona
    {
      id: "strathcona",
      lat: 49.2798,
      lng: -123.0915,
      popupText: "Strathcona – Historic East End"
    },
    // Chinatown
    {
      id: "chinatown",
      lat: 49.2806,
      lng: -123.1058,
      popupText: "Historic Chinatown"
    },
    // Punjabi Market
    {
      id: "punjabi-market",
      lat: 49.2257,
      lng: -123.1016,
      popupText: "Punjabi Market (Little India)"
    },
    // Commercial Drive
    {
      id: "commercial-drive",
      lat: 49.2690,
      lng: -123.0690,
      popupText: "Commercial Drive (Little Italy)"
    },
    // Mount Pleasant
    {
      id: "mount-pleasant",
      lat: 49.2625,
      lng: -123.0953,
      popupText: "Mount Pleasant (South Main)"
    },
    // Southlands
    {
      id: "southlands",
      lat: 49.2260,
      lng: -123.1735,
      popupText: "Southlands – Urban Countryside"
    },
    // Hastings-Sunrise
    {
      id: "hastings-sunrise",
      lat: 49.2905,
      lng: -123.0379,
      popupText: "Hastings-Sunrise & New Brighton Park"
    },
    // Queen Elizabeth Park
    {
      id: "queen-elizabeth-park",
      lat: 49.2417,
      lng: -123.1121,
      popupText: "Queen Elizabeth Park"
    },
    // Pacific Spirit Park & Wreck Beach
    {
      id: "pacific-spirit-park",
      lat: 49.2497,
      lng: -123.2503,
      popupText: "Pacific Spirit Park & Wreck Beach"
    },
    // Kerrisdale
    {
      id: "kerrisdale",
      lat: 49.2340,
      lng: -123.1550,
      popupText: "Kerrisdale"
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Hidden Vancouver Neighborhoods Revealed</title>
        <meta
          name="description"
          content="Explore Vancouver's hidden neighborhoods—historic, cultural, and natural gems—offering real-world tips, step-by-step advice, and essential data."
        />
        <meta
          name="keywords"
          content="Vancouver, hidden neighborhoods, guide, travel tips, Strathcona, Chinatown, Punjabi Market, Commercial Drive, Mount Pleasant"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/hidden-vancouver-neighborhoods.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Hidden Vancouver Neighborhoods Revealed"
        />
        <meta
          property="og:description"
          content="Discover Vancouver’s off-the-beaten-path neighborhoods with useful tips, local insights, and practical details."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/hidden-vancouver-neighborhoods.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/hidden-vancouver-neighborhoods.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Hidden Vancouver Neighborhoods Revealed"
        />
        <meta
          name="twitter:description"
          content="Step beyond the standard tourist routes and uncover Vancouver’s lesser-known districts."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/hidden-vancouver-neighborhoods.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt="Explore Vancouver's hidden neighborhoods"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia/>

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s famous sites—Stanley Park, Gastown, Granville 
              Island—often overshadow its lesser-known gems. Yet, this 
              coastal city is a tapestry of neighborhoods, each with 
              unique stories, cultural diversity, and hidden charm. 
              From historic Strathcona to the equestrian fields of 
              Southlands, venturing off the tourist trail reveals the 
              city’s authentic character. In this guide, you’ll find 
              actionable tips, addresses, operating hours, approximate 
              costs, and more, so you can explore Vancouver like a 
              local. 
            </p>

            {/* Strathcona */}
            <h2 id="strathcona">Strathcona</h2>
            <p
              ref={(el) => setSpotRef("strathcona", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 
              <Marker
                coordY={49.2798}
                coordX={-123.0915}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              (Latitude: 49.2798, Longitude: -123.0915)
            </p>
            <p>
              Vancouver’s oldest residential neighborhood, Strathcona 
              boasts Victorian and Edwardian homes, tree-lined streets, 
              and a resilient community spirit. Stop by the Strathcona 
              Community Garden (759 Malkin Ave, dawn–dusk, free entry) 
              or peek into heritage corners like the Union Market 
              (810 Union St.). This is a walkable area—just be mindful 
              of uneven sidewalks. By evening, consider Strathcona Beer 
              Company (895 E Hastings St.) for local craft brews 
              (flights ~$9). 
            </p>

            {/* Chinatown */}
            <h2 id="chinatown">Chinatown</h2>
            <p
              ref={(el) => setSpotRef("chinatown", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 
              <Marker
                coordY={49.2806}
                coordX={-123.1058}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              (Latitude: 49.2806, Longitude: -123.1058)
            </p>
            <p>
              One of the largest historic Chinatowns in North America, 
              Vancouver’s Chinatown features the iconic Millennium Gate 
              at Pender and Taylor. Wander past markets full of vibrant 
              produce and sample dim sum at Jade Dynasty (137 E Pender 
              St., 9am–3pm, ~$5–7 per dish). Explore the Dr. Sun 
              Yat-Sen Classical Chinese Garden (578 Carrall St., 
              ~$16 entry), or take in the free adjacent public park 
              next door. Visit in late January or February for the 
              lively Lunar New Year Parade. 
            </p>

            {/* Punjabi Market */}
            <h2 id="punjabi-market">Punjabi Market</h2>
            <p
              ref={(el) => setSpotRef("punjabi-market", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 
              <Marker
                coordY={49.2257}
                coordX={-123.1016}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              (Latitude: 49.2257, Longitude: -123.1016)
            </p>
            <p>
              Centered at Main St. and E 49th Ave, the Punjabi Market 
              area (Little India) teems with shops selling Indian 
              sweets, spices, and traditional clothing. Grab a box of 
              fresh jalebis or ladoos at Punjabi Market Sweet Shop 
              (6635 Main St.), or treat yourself to a flavorful lunch 
              special (~$12) at Himalaya Restaurant (6587 Main St.). 
              Don’t miss the colorful murals celebrating South Asian 
              heritage. 
            </p>

            {/* Commercial Drive */}
            <h2 id="commercial-drive">Commercial Drive</h2>
            <p
              ref={(el) => setSpotRef("commercial-drive", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 
              <Marker
                coordY={49.2690}
                coordX={-123.0690}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              (Latitude: 49.2690, Longitude: -123.0690)
            </p>
            <p>
              Known simply as “The Drive,” this Little Italy district 
              offers a multicultural blend of global cuisine, indie 
              boutiques, and an artsy vibe. Sample an espresso at 
              Joe’s Café (1150 Commercial Dr.), savor pizza at Marcello 
              (1404 Commercial Dr.), or catch live music at the historic 
              Rio Theatre (1660 E Broadway). Events like “Italian Day 
              on The Drive” in June fill the street with food stalls 
              and celebrations. 
            </p>

            {/* Mount Pleasant */}
            <h2 id="mount-pleasant">Mount Pleasant</h2>
            <p
              ref={(el) => setSpotRef("mount-pleasant", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 
              <Marker
                coordY={49.2625}
                coordX={-123.0953}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              (Latitude: 49.2625, Longitude: -123.0953)
            </p>
            <p>
              Also called South Main, Mount Pleasant is home to craft 
              breweries, third-wave coffee shops, and vibrant street 
              art. Don’t miss murals in the alleyways near Main & E 5th 
              to E 7th Ave. Enjoy brunch at Jam Café (556 Beatty St.) 
              or sample tasting flights at Brassneck Brewery (2148 Main 
              St., ~$7–10). The neighborhood is easily accessed by the 
              99 B-Line bus. 
            </p>

            {/* Southlands */}
            <h2 id="southlands">Southlands</h2>
            <p
              ref={(el) => setSpotRef("southlands", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 
              <Marker
                coordY={49.2260}
                coordX={-123.1735}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              (Latitude: 49.2260, Longitude: -123.1735)
            </p>
            <p>
              A pastoral escape in the city’s southwest corner, 
              Southlands offers horse farms, riding trails, and the 
              Southlands Heritage Farm (6767 Balaclava St.). Stroll 
              down Celtic Avenue to the riverfront, where you can enjoy 
              Deering Island Park for a quiet picnic. Free to explore; 
              riding lessons are available at local stables (~$50–80). 
              Watch for horses on the road—this is Vancouver’s equestrian 
              country! 
            </p>

            {/* Hastings-Sunrise */}
            <h2 id="hastings-sunrise">Hastings-Sunrise & New Brighton Park</h2>
            <p
              ref={(el) => setSpotRef("hastings-sunrise", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 
              <Marker
                coordY={49.2905}
                coordX={-123.0379}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              (Latitude: 49.2905, Longitude: -123.0379)
            </p>
            <p>
              The East Village area, near the Pacific National Exhibition 
              (PNE), features Playland amusement park (2901 E Hastings 
              St., ~$33 day pass) in summer and the annual PNE Fair in 
              August. For a quieter moment, head to New Brighton Park 
              (free entry, heated outdoor pool ~$6 in summer). Parallel 
              49 Brewing (1950 Triumph St.) and Container Brewing 
              (1216 Franklin St.) offer local craft beer spots for you 
              to enjoy. 
            </p>

            {/* Queen Elizabeth Park */}
            <h2 id="queen-elizabeth-park">Queen Elizabeth Park</h2>
            <p
              ref={(el) => setSpotRef("queen-elizabeth-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 
              <Marker
                coordY={49.2417}
                coordX={-123.1121}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              (Latitude: 49.2417, Longitude: -123.1121)
            </p>
            <p>
              This 130-acre park is the highest point in Vancouver, 
              boasting panoramic views. Check out the Quarry Gardens 
              and Bloedel Conservatory (~$8 entry), a geodesic dome 
              housing exotic birds and tropical plants. Parking (~$3–4 
              per hour) is available, or walk from King Edward or 
              Oakridge SkyTrain stations. Ideal for a sunset photo 
              session or a relaxing picnic. 
            </p>

            {/* Pacific Spirit Park */}
            <h2 id="pacific-spirit-park">Pacific Spirit Park & Wreck Beach</h2>
            <p
              ref={(el) => setSpotRef("pacific-spirit-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 
              <Marker
                coordY={49.2497}
                coordX={-123.2503}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              (Latitude: 49.2497, Longitude: -123.2503)
            </p>
            <p>
              Enveloping the University of British Columbia, Pacific 
              Spirit Park offers 70+ km of forested trails (free, dawn 
              to dusk). Access Wreck Beach (Trail 6 near UBC), a 
              clothing-optional shoreline with steep steps and gorgeous 
              ocean views. Pack out your trash and respect others’ 
              privacy—no photography allowed without consent. 
            </p>

            {/* Kerrisdale */}
            <h2 id="kerrisdale">Kerrisdale</h2>
            <p
              ref={(el) => setSpotRef("kerrisdale", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Location:</strong> 
              <Marker
                coordY={49.2340}
                coordX={-123.1550}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              (Latitude: 49.2340, Longitude: -123.1550)
            </p>
            <p>
              A quaint, upscale residential area with a “village” feel, 
              Kerrisdale’s main hub is along West 41st Avenue. Enjoy 
              a coffee and shortbread at Moore’s Bakery (2128 W 41st 
              Ave) or browse boutiques and independent bookstores. 
              Don’t miss Kerrisdale’s Farmers Market (Saturdays, Jun–Oct, 
              10am–2pm) for fresh produce and crafts. 
            </p>

            {/* FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Are these neighborhoods safe for tourists?</h3>
              <p>
                Overall, yes. Vancouver is generally safe; just exercise 
                normal precautions—be mindful at night or when exploring 
                less busy streets. Locals are friendly and used to 
                visitors exploring these neighborhoods.
              </p>

              <h3>2. Which transit options do I have?</h3>
              <p>
                Bus, SkyTrain, and bike are popular. Most areas (like 
                Chinatown, Commercial Drive, Mount Pleasant, Queen 
                Elizabeth Park) are accessible via SkyTrain or frequent 
                bus routes. UBC-bound buses serve Pacific Spirit Park; 
                Southlands might require a short walk from the #49 bus. 
              </p>

              <h3>3. What’s the best season to visit?</h3>
              <p>
                Any season. Summer offers festivals and warm beaches, 
                while spring features cherry blossoms. Fall has crisp 
                air and autumn colors, and winter events like Chinese 
                New Year make for unique cultural experiences. Dress 
                for rain—Vancouver can be wet any time of year.
              </p>
            </div>

            {/* Local Regulations */}
            <h2 id="local-regulations">Local Regulations & Best Practices</h2>
            <p>
              Vancouver parks generally close at 10pm (some earlier). 
              No smoking or vaping is allowed in parks or on beaches. 
              Pack out your litter—Leave No Trace. 
              Clothing-optional areas, like Wreck Beach, request a 
              photography-free policy. In residential zones, obey 
              posted speed limits and parking rules. 
            </p>

            {/* Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Exploring these lesser-known areas of Vancouver will 
              show you a city filled with cultural depth, historical 
              stories, and natural beauty beyond the standard downtown 
              routes. Whether strolling through historic streets in 
              Strathcona or enjoying a sunset at Wreck Beach, you’ll 
              discover authentic local life at every turn. Lace up 
              your walking shoes, hop on public transit, and experience 
              Vancouver’s hidden treasures for yourself!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom
              referencesData={referencesData}
              ref_data={ref_data}
            />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}